import React, { useState, useEffect, useCallback } from 'react';
import Header from '../Header';
import '../scss/playerAttendance.scss';
import api from '../services/apiService';

const PlayerAttendance = () => {
    const [players, setPlayers] = useState([]);
    const [hasTraining, setHasTraining] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [filterDays, setFilterDays] = useState(30); // Default to 30 days
    const [error, setError] = useState(null);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [matchDetails, setMatchDetails] = useState([]);
    const [showMatchModal, setShowMatchModal] = useState(false);
    const [selectedType, setSelectedType] = useState('all');
    const [tooltipData, setTooltipData] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [isMobile, setIsMobile] = useState(false);
    const [showDummyData, setShowDummyData] = useState(false);

    const teamId = sessionStorage.getItem('selected_team_id');

    // Generate dummy attendance data for demonstration
    const generateDummyAttendanceData = () => {
        const dummyPlayers = [
            {
                player_id: '1',
                player_name: 'Demo Player 1',
                last_played_date: '2025-07-30',
                last_login: '2025-08-01',
                no_response_count: 2,
                training: {
                    sessions: generateDummySessions(30, 'training')
                },
                matches: {
                    sessions: generateDummySessions(30, 'match')
                }
            },
            {
                player_id: '2',
                player_name: 'Demo Player 2',
                last_played_date: '2025-07-28',
                last_login: '2025-07-31',
                no_response_count: 0,
                training: {
                    sessions: generateDummySessions(30, 'training')
                },
                matches: {
                    sessions: generateDummySessions(30, 'match')
                }
            },
            {
                player_id: '3',
                player_name: 'Demo Player 3',
                last_played_date: null,
                last_login: null,
                no_response_count: 8,
                training: {
                    sessions: generateDummySessions(30, 'training')
                },
                matches: {
                    sessions: generateDummySessions(30, 'match')
                }
            }
        ];

        return dummyPlayers;
    };

    const generateDummySessions = (days, type) => {
        const sessions = [];
        const today = new Date();
        
        for (let i = days - 1; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            
            // Skip some days to create realistic gaps
            if (type === 'training') {
                // Training sessions on Tuesdays and Thursdays
                const dayOfWeek = date.getDay();
                if (dayOfWeek !== 2 && dayOfWeek !== 4) continue; // Skip non-Tuesday/Thursday
            } else {
                // Matches on Saturdays
                const dayOfWeek = date.getDay();
                if (dayOfWeek !== 6) continue; // Skip non-Saturday
            }
            
            // Create realistic response patterns
            let response;
            const random = Math.random();
            
            if (random < 0.7) {
                response = 'Yes'; // 70% attendance
            } else if (random < 0.85) {
                response = 'No'; // 15% decline
            } else {
                response = null; // 15% no response
            }
            
            sessions.push({
                match_id: `dummy_${type}_${i}`,
                date: date.toISOString().split('T')[0],
                time: type === 'training' ? '18:00' : '14:00',
                response: response,
                type: type,
                venue: type === 'training' ? 'Training Ground' : 'Home Pitch',
                opposition_team: type === 'match' ? 'Away Team' : null,
                score_display: type === 'match' ? '2-1' : null
            });
        }
        
        return sessions;
    };

    const fetchAttendanceData = useCallback(async (days) => {
        try {
            setIsLoading(true);
            setError(null);
            
            const response = await api.get(`${process.env.REACT_APP_API_URL}/get_player_attendance_timeline.php`, {
                params: {
                    team_id: teamId,
                    filter_days: days
                }
            });

            if (response.data.success) {
                // Check if we have real data
                if (response.data.data.players && response.data.data.players.length > 0) {
                    setPlayers(response.data.data.players);
                    setHasTraining(response.data.data.has_training);
                    setShowDummyData(false);
                    console.log('API Response - has_training:', response.data.data.has_training);
                } else {
                    // Show dummy data when no real data is available
                    console.log('No real data found, showing dummy data');
                    const dummyPlayers = generateDummyAttendanceData();
                    setPlayers(dummyPlayers);
                    setHasTraining(true);
                    setShowDummyData(true);
                }
            } else {
                // Show dummy data on API error
                console.log('API returned success: false, showing dummy data');
                const dummyPlayers = generateDummyAttendanceData();
                setPlayers(dummyPlayers);
                setHasTraining(true);
                setShowDummyData(true);
            }
        } catch (error) {
            console.error('Error fetching attendance data:', error);
            // Show dummy data on error
            console.log('Error occurred, showing dummy data');
            const dummyPlayers = generateDummyAttendanceData();
            setPlayers(dummyPlayers);
            setHasTraining(true);
            setShowDummyData(true);
        } finally {
            setIsLoading(false);
        }
    }, [teamId]);

    const fetchMatchDetails = useCallback(async (playerId, type) => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/get_player_match_details.php`, {
                params: {
                    player_id: playerId,
                    team_id: teamId,
                    filter_days: filterDays,
                    type: type
                }
            });

            if (response.data.success) {
                setMatchDetails(response.data.data.matches);
                setShowMatchModal(true);
            }
        } catch (error) {
            console.error('Error fetching match details:', error);
        }
    }, [teamId, filterDays]);

    useEffect(() => {
        if (teamId) {
            fetchAttendanceData(filterDays);
        } else {
            setError('Team ID not found');
            setIsLoading(false);
        }
    }, [teamId, filterDays, fetchAttendanceData]);

    // Detect mobile device
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleSquareHover = (event, session) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const tooltipWidth = 200; // Approximate tooltip width
        
        let x = rect.left + rect.width / 2;
        
        // Ensure tooltip doesn't go off screen
        if (x + tooltipWidth / 2 > viewportWidth) {
            x = viewportWidth - tooltipWidth / 2 - 10;
        } else if (x - tooltipWidth / 2 < 0) {
            x = tooltipWidth / 2 + 10;
        }
        
        setTooltipPosition({
            x: x,
            y: rect.bottom + 10
        });
        setTooltipData({ session });
    };

    const handleSquareClick = (event, session) => {
        // Toggle tooltip - if same session is clicked, hide it
        if (tooltipData && tooltipData.session && tooltipData.session.match_id === session.match_id) {
            setTooltipData(null);
            return;
        }
        
        const rect = event.currentTarget.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const tooltipWidth = 200; // Approximate tooltip width
        
        let x = rect.left + rect.width / 2;
        
        // Ensure tooltip doesn't go off screen
        if (x + tooltipWidth / 2 > viewportWidth) {
            x = viewportWidth - tooltipWidth / 2 - 10;
        } else if (x - tooltipWidth / 2 < 0) {
            x = tooltipWidth / 2 + 10;
        }
        
        setTooltipPosition({
            x: x,
            y: rect.bottom + 10
        });
        setTooltipData({ session });
    };

    const renderMonthHeaders = (sessions) => {
        if (!sessions || !Array.isArray(sessions) || sessions.length === 0) return null;
        
        const monthHeaders = [];
        let currentMonth = null;
        let currentMonthCount = 0;
        let startIndex = 0;
        
        sessions.forEach((session, index) => {
            const date = new Date(session.date);
            const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
            
            if (monthKey !== currentMonth) {
                // Add previous month header if exists
                if (currentMonth !== null) {
                    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
                    const monthDate = new Date(sessions[startIndex].date);
                    const monthName = monthNames[monthDate.getMonth()];
                    const year = monthDate.getFullYear();
                    
                    monthHeaders.push({
                        month: `${monthName} '${year.toString().slice(-2)}`,
                        count: currentMonthCount,
                        startIndex: startIndex,
                        isAlternate: monthHeaders.length % 2 === 1
                    });
                }
                
                currentMonth = monthKey;
                currentMonthCount = 1;
                startIndex = index;
            } else {
                currentMonthCount++;
            }
        });
        
        // Add the last month
        if (currentMonth !== null) {
            const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
            const monthDate = new Date(sessions[startIndex].date);
            const monthName = monthNames[monthDate.getMonth()];
            const year = monthDate.getFullYear();
            
            monthHeaders.push({
                month: `${monthName} '${year.toString().slice(-2)}`,
                count: currentMonthCount,
                startIndex: startIndex,
                isAlternate: monthHeaders.length % 2 === 1
            });
        }
        
        return monthHeaders;
    };

    const renderAttendanceBar = (sessions, color, label, playerId, type) => {
        // Add null/undefined check for sessions
        if (!sessions || !Array.isArray(sessions)) {
            return null;
        }
        
        const monthHeaders = renderMonthHeaders(sessions);
        
        return (
            <div className="attendance-bar-container">
                {/* Month Headers */}
                <div className="month-headers">
                    {monthHeaders && monthHeaders.map((header, index) => (
                        <div
                            key={index}
                            className={`month-header ${header.isAlternate ? 'alternate' : ''}`}
                            style={{
                                flex: header.count,
                                minWidth: `${header.count * 32}px`
                            }}
                        >
                            {header.month}
                        </div>
                    ))}
                </div>
                
                <div 
                    className="attendance-bar"
                    onClick={() => {
                        setSelectedPlayer(players.find(p => p.player_id === playerId));
                        setSelectedType(type);
                        fetchMatchDetails(playerId, type);
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    {sessions.map((session) => {
                        const date = new Date(session.date);
                        const day = date.getDate();
                        
                        const getSquareClass = () => {
                            if (session.response === 'Yes') return 'filled';
                            if (session.response === 'No') return 'empty';
                            return 'not-responded';
                        };
                        
                        return (
                            <div
                                key={session.match_id}
                                className={`attendance-square ${getSquareClass()}`}
                                style={{ backgroundColor: session.response === 'Yes' ? color : '#333' }}
                                onMouseEnter={(e) => !isMobile && handleSquareHover(e, session)}
                                onMouseLeave={() => !isMobile && setTooltipData(null)}
                                onClick={(e) => isMobile && handleSquareClick(e, session)}
                            >
                                {day}
                            </div>
                        );
                    })}
                </div>
            
            {/* Match Details Modal */}
            {showMatchModal && (
                <div className="match-modal-overlay" onClick={() => setShowMatchModal(false)}>
                    <div className="match-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{selectedPlayer?.player_name} - {selectedType === 'training' ? 'Training Sessions' : selectedType === 'match' ? 'Matches' : 'All Sessions'}</h3>
                            <button className="close-button" onClick={() => setShowMatchModal(false)}>×</button>
                        </div>
                        <div className="modal-content">
                            {matchDetails.length === 0 ? (
                                <p>No sessions found for this period.</p>
                            ) : (
                                <div className="match-list">
                                    {matchDetails.map((match) => (
                                        <div key={match.match_id} className="match-item">
                                            <div className="match-date">
                                                {new Date(match.date).toLocaleDateString('en-GB')}
                                                {match.time && ` at ${match.time}`}
                                            </div>
                                            <div className="match-details">
                                                <div className="match-type">{match.type === 'training' ? 'Training' : 'Match'}</div>
                                                {match.venue && <div className="match-venue">{match.venue}</div>}
                                                {match.opposition_team && <div className="match-opposition">vs {match.opposition_team}</div>}
                                                {match.score_display && <div className="match-score">{match.score_display}</div>}
                                                <div className={`match-response ${match.response?.toLowerCase()}`}>
                                                    {match.response || 'No response'}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

    if (isLoading) {
        return (
            <div className="player-attendance-container container">
                <Header />
                <div className="loading">Loading attendance data...</div>
            </div>
        );
    }

    if (error && !showDummyData) {
        return (
            <div className="player-attendance-container container">
                <Header />
                <div className="error">Error: {error}</div>
            </div>
        );
    }

    if ((!players || players.length === 0) && !showDummyData) {
        return (
            <div className="player-attendance-container container">
                <Header />
                <div className="error">No players found for this team</div>
            </div>
        );
    }

    return (
        <div className="player-attendance-container container">
            <Header />
            <h1 className="page-title">Player Attendance</h1>
            
            {showDummyData && (
                <div className="dummy-data-notice global-notice">
                    <h4>Welcome to FirstWhistle!</h4>
                    <p>We're showing example attendance data to help you experience the app.
                    Your team's attendance data will appear here once players have responded to training sessions and matches.</p>
                </div>
            )}
            
            <div className="filter-controls">
                <button 
                    className={`filter-button ${filterDays === 30 ? 'active' : ''}`}
                    onClick={() => {
                        setFilterDays(30);
                        setTooltipData(null); // Hide tooltip when filter changes
                    }}
                >
                    30 Days
                </button>
                <button 
                    className={`filter-button ${filterDays === 60 ? 'active' : ''}`}
                    onClick={() => {
                        setFilterDays(60);
                        setTooltipData(null); // Hide tooltip when filter changes
                    }}
                >
                    60 Days
                </button>
                <button 
                    className={`filter-button ${filterDays === 90 ? 'active' : ''}`}
                    onClick={() => {
                        setFilterDays(90);
                        setTooltipData(null); // Hide tooltip when filter changes
                    }}
                >
                    90 Days
                </button>
            </div>
            
            <div className="attendance-key">
                <div className="key-item">
                    <div className="key-square played"></div>
                    <span>Played</span>
                </div>
                <div className="key-item">
                    <div className="key-square not-played"></div>
                    <span>Not Played</span>
                </div>
                <div className="key-item">
                    <div className="key-square not-responded"></div>
                    <span>Not Responded</span>
                </div>
                <div className="key-item">
                    <div className="key-square training-attended"></div>
                    <span>Training Attended</span>
                </div>
                <div className="key-item">
                    <div className="key-square training-declined"></div>
                    <span>Training Declined</span>
                </div>
            </div>
            
            <div className="players-list">
                {players.map((player) => (
                    <div key={player.player_id} className="player-card">
                        <div className="player-info">
                            <div className="player-name">{player.player_name}</div>
                            <div className="player-details">
                                {player.last_played_date && (
                                    <span className="last-played">
                                        Last played: {new Date(player.last_played_date).toLocaleDateString('en-GB')}
                                    </span>
                                )}
                                {player.last_played_date && (
                                    <span className="separator">•</span>
                                )}
                                <span className="last-login">
                                    Last login: {player.last_login ? new Date(player.last_login).toLocaleDateString('en-GB') : 'Never logged in'}
                                </span>

                                {player.no_response_count === 0 && player.matches && player.matches.sessions && player.matches.sessions.length > 0 && 
                                 player.matches.sessions.every(s => !s.response) && (
                                    <>
                                        {(player.last_played_date || player.last_login) && (
                                            <span className="separator">•</span>
                                        )}
                                        <span className="no-app-activity">
                                            <strong>⚠️ No app activity - not responding to invites</strong>
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        <div className="attendance-bars-scroll">
                            <div className="attendance-bars">
                            {hasTraining && (
                                <>
                                    <div className="session-heading">
                                        <span className="heading-text training">Training</span>
                                        <span className="attendance-counter">
                                            Attended: {player.training && player.training.sessions ? player.training.sessions.filter(s => s.response === 'Yes').length : 0}/{player.training && player.training.sessions ? player.training.sessions.length : 0}
                                        </span>
                                    </div>
                                    {renderAttendanceBar(
                                        player.training && player.training.sessions ? player.training.sessions : [],
                                        '#3b82f6',
                                        'Training',
                                        player.player_id,
                                        'training'
                                    )}
                                </>
                            )}
                            
                            <div className="session-heading">
                                <span className="heading-text matches">Matches</span>
                                <span className="attendance-counter">
                                    Attended: {player.matches && player.matches.sessions ? player.matches.sessions.filter(s => s.response === 'Yes').length : 0}/{player.matches && player.matches.sessions ? player.matches.sessions.length : 0}
                                </span>
                            </div>
                            {renderAttendanceBar(
                                player.matches.sessions,
                                '#10b981',
                                'Matches',
                                player.player_id,
                                'match'
                            )}
                            </div>
                        </div>
                        

                    </div>
                ))}
            </div>
            
            {/* Tooltip */}
            {tooltipData && (
                <div 
                    className="attendance-tooltip"
                    style={{
                        position: 'fixed',
                        left: tooltipPosition.x,
                        top: tooltipPosition.y,
                        transform: 'translateX(-50%)',
                        zIndex: 1001
                    }}
                >
                    <div className="tooltip-content">
                        {tooltipData.session ? (
                            <>
                                <div className="tooltip-date">
                                    {new Date(tooltipData.session.date).toLocaleDateString('en-GB')}
                                    {tooltipData.session.time && ` at ${tooltipData.session.time}`}
                                </div>
                                {tooltipData.session.opposition_team && (
                                    <div className="tooltip-opposition">
                                        vs {tooltipData.session.opposition_team}
                                    </div>
                                )}
                                <div className="tooltip-response">
                                    Response: {tooltipData.session.response || 'No response'}
                                </div>
                            </>
                        ) : (
                            <div>Loading...</div>
                        )}
                    </div>
                    <div className="tooltip-arrow"></div>
                </div>
            )}
        </div>
    );
};

export default PlayerAttendance; 